import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material'
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Info as InfoIcon,
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  Home as HomeIcon,
  LightMode,
  DarkMode,
  SettingsBrightness,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { useThemeMode, THEME_MODES } from '../contexts/ThemeContext'
import CreateTicketDialog from './CreateTicketDialog'
import AboutModal from './AboutModal'
import wefiLogo from '../assets/wefi-logo.svg'
import agentFlowLogo from '../assets/AgentFlow.svg'
import { useScreenSaver } from '../contexts/ScreenSaverContext'

const Layout = ({ children }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { themeMode, setThemeMode } = useThemeMode()
  const { activateScreenSaver } = useScreenSaver()

  const [anchorEl, setAnchorEl] = useState(null)
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const [createTicketOpen, setCreateTicketOpen] = useState(false)
  const [aboutModalOpen, setAboutModalOpen] = useState(false)

  // Check if user has admin role (from token OR application roles)
  const hasTokenAdminRole = user?.roles?.some(role =>
    role.toLowerCase() === 'agentflow.admin'
  )
  const hasApplicationAdminRole = user?.application_roles?.some(role =>
    role.toLowerCase() === 'application.admin'
  )
  const isAdmin = hasTokenAdminRole || hasApplicationAdminRole

  // Check if user has user role (from token OR application roles)
  const hasTokenUserRole = user?.roles?.some(role =>
    role.toLowerCase() === 'agentflow.user'
  )
  const hasApplicationUserRole = user?.application_roles?.some(role =>
    role.toLowerCase() === 'application.user'
  )
  const isUser = hasTokenUserRole || hasApplicationUserRole || isAdmin // Admins are automatically users

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    logout()
    handleProfileMenuClose()
  }

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen)
  }

  const handleThemeMenuOpen = (event) => {
    setThemeMenuAnchor(event.currentTarget)
  }

  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null)
  }

  const handleThemeChange = (newTheme) => {
    setThemeMode(newTheme)
    handleThemeMenuClose()
  }

  const getThemeIcon = (mode) => {
    switch (mode) {
      case THEME_MODES.LIGHT:
        return <LightMode />
      case THEME_MODES.NIGHT:
        return <DarkMode />
      case THEME_MODES.SYSTEM:
        return <SettingsBrightness />
      default:
        return <LightMode />
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" elevation={1}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleMobileDrawer}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Box
            sx={{
              display: isMobile ? 'none' : 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
          >
            <img
              src={wefiLogo}
              alt="WefiTec"
              style={{
                height: isMobile ? '24px' : '30px',
                marginRight: '12px'
              }}
            />
          </Box>
          
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
            <img
              src={agentFlowLogo}
              alt="AgentFlow"
              style={{
                height: isMobile ? '28px' : '36px',
                width: 'auto'
              }}
              onClick={() => navigate('/')}
              onDoubleClick={() => {
                // Double-click activates screen saver for testing (hidden feature)
                activateScreenSaver()
              }}
            />
          </Box>

          {/* Desktop Actions */}
          {!isMobile && (
            <IconButton
              color="inherit"
              onClick={() => setCreateTicketOpen(true)}
              sx={{ mr: 2 }}
            >
              <AddIcon />
            </IconButton>
          )}

          {/* Desktop Only - Theme Switcher */}
          {!isMobile && (
            <IconButton
              color="inherit"
              onClick={handleThemeMenuOpen}
              sx={{ mr: 1 }}
              aria-label="switch theme"
            >
              {getThemeIcon(themeMode)}
            </IconButton>
          )}

          {/* Desktop Only - Profile Menu */}
          {!isMobile && (
            <IconButton
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        id="primary-search-account-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        <MenuItem disabled>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {user?.name || user?.email || 'User'}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          navigate('/analytics')
          handleProfileMenuClose()
        }}>
          <AnalyticsIcon sx={{ mr: 1 }} />
          Analytics Dashboard
        </MenuItem>
        {isAdmin && (
          <>
            <MenuItem onClick={() => {
              navigate('/admin')
              handleProfileMenuClose()
            }}>
              <AdminIcon sx={{ mr: 1 }} />
              Admin Dashboard
            </MenuItem>
            <Divider />
          </>
        )}
        <MenuItem onClick={() => {
          setAboutModalOpen(true)
          handleProfileMenuClose()
        }}>
          <InfoIcon sx={{ mr: 1 }} />
          About
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Logout sx={{ mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>

      {/* Theme Switcher Menu */}
      <Menu
        anchorEl={themeMenuAnchor}
        open={Boolean(themeMenuAnchor)}
        onClose={handleThemeMenuClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          onClick={() => handleThemeChange(THEME_MODES.LIGHT)}
          selected={themeMode === THEME_MODES.LIGHT}
        >
          <LightMode sx={{ mr: 1 }} />
          Light
        </MenuItem>
        <MenuItem 
          onClick={() => handleThemeChange(THEME_MODES.NIGHT)}
          selected={themeMode === THEME_MODES.NIGHT}
        >
          <DarkMode sx={{ mr: 1 }} />
          Night
        </MenuItem>
        <MenuItem 
          onClick={() => handleThemeChange(THEME_MODES.SYSTEM)}
          selected={themeMode === THEME_MODES.SYSTEM}
        >
          <SettingsBrightness sx={{ mr: 1 }} />
          System
        </MenuItem>
      </Menu>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={toggleMobileDrawer}
        sx={{ display: { md: 'none' } }}
      >
        <Box sx={{ width: 280 }}>
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <Typography variant="h6">
              AgentFlow
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {user?.email}
            </Typography>
          </Box>
          
          <List>
            <ListItem disablePadding>
              <ListItemButton 
                selected={location.pathname === '/'}
                onClick={() => {
                  navigate('/')
                  setMobileDrawerOpen(false)
                }}
              >
                <ListItemIcon>
                  <HomeIcon />
                </ListItemIcon>
                <ListItemText primary="Tickets" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => {
                setCreateTicketOpen(true)
                setMobileDrawerOpen(false)
              }}>
                <ListItemIcon>
                  <AddIcon />
                </ListItemIcon>
                <ListItemText primary="New Ticket" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton 
                selected={location.pathname === '/analytics'}
                onClick={() => {
                  navigate('/analytics')
                  setMobileDrawerOpen(false)
                }}
              >
                <ListItemIcon>
                  <AnalyticsIcon />
                </ListItemIcon>
                <ListItemText primary="Analytics" />
              </ListItemButton>
            </ListItem>

            {isAdmin && (
              <>
                <Divider sx={{ my: 1 }} />
                <ListItem disablePadding>
                  <ListItemButton
                    selected={location.pathname.startsWith('/admin')}
                    onClick={() => {
                      navigate('/admin')
                      setMobileDrawerOpen(false)
                    }}
                  >
                    <ListItemIcon>
                      <AdminIcon />
                    </ListItemIcon>
                    <ListItemText primary="Admin Dashboard" />
                  </ListItemButton>
                </ListItem>
              </>
            )}
            
            {/* Theme Switcher and Logout in Mobile Drawer */}
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton onClick={handleThemeMenuOpen}>
                <ListItemIcon>
                  {getThemeIcon(themeMode)}
                </ListItemIcon>
                <ListItemText primary="Theme" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => {
                handleLogout()
                setMobileDrawerOpen(false)
              }}>
                <ListItemIcon>
                  <Logout />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default' }}>
        {children}
      </Box>

      {/* Create Ticket Dialog */}
      <CreateTicketDialog
        open={createTicketOpen}
        onClose={() => setCreateTicketOpen(false)}
      />

      {/* About Modal */}
      <AboutModal
        open={aboutModalOpen}
        onClose={() => setAboutModalOpen(false)}
      />
    </Box>
  )
}

export default Layout